import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  type SxProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { useNavigate } from "react-router-dom";
import type { Theme } from "@mui/material/styles";
import { Calendar } from "lucide-react";
import { GET_CLASS_LIST, GET_EVENT_LIST, GET_MY_EVENT_BOOKINGS, GET_MY_PURCHASED_PASSES, ME } from "../graphql/query";
import { formatTo12Hour } from "../utils/time";
import themestyle from "../theme";

type MiniCard = {
  label: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  path?: string;
  state?: any;
};

type TodayClassRow = {
  id: string;
  time: string;
  className: string;
  instructor: string;
  status: "Upcoming" | "Ongoing" | "Completed";
  startsAt: Date;
};

type UpcomingEventCardRow = {
  id: string;
  month: string;
  day: string;
  title: string;
  subtitle: string;
  tag: string;
  tagColor: string;
};

type DashboardPassCard = {
  id: string;
  title: string;
  subtitle: string;
  chipLabel: string;
  chipBg: string;
  chipColor: string;
  remainingChipLabel?: string;
  remainingChipBg?: string;
  remainingChipColor?: string;
  metaLabel: string;
  metaValue: string;
  progressValue: number;
  progressColor: string;
  footer: string;
  expiryText: string;
};

function CardShell({ children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Card
      elevation={0}
      sx={{ border: `1px solid ${themestyle.colors.bordercolor}`, borderRadius: 2, ...sx }}
    >
      {children}
    </Card>
  );
}

function statusChip(status: "Upcoming" | "Ongoing" | "Completed") {
  if (status === "Upcoming") {
    return (
      <Chip
        size="small"
        label="Upcoming"
        sx={{ height: 20, fontWeight: 900, bgcolor: "#f0f9e4", color: "#a3d65c", fontSize: 11 }}
      />
    );
  }
  if (status === "Ongoing") {
    return (
      <Chip
        size="small"
        label="Ongoing"
        sx={{ height: 20, fontWeight: 900, bgcolor: "#E8F1FF", color: "#2563EB", fontSize: 11 }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label="Completed"
      sx={{ height: 20, fontWeight: 900, bgcolor: "#EEF2F7", color: "#64748B", fontSize: 11 }}
    />
  );
}

function formatLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getPassTypeLabel(passType?: string | null) {
  if (!passType) return "PASS";
  const normalized = passType.toUpperCase();
  if (normalized === "DROP_IN" || normalized === "DROP-IN") return "DROP IN";
  if (normalized === "WEEKLY" || normalized === "WEEKLY_4_CLASSES") return "WEEKLY";
  if (normalized === "MONTHLY" || normalized === "MONTHLY_4_CLASSES") return "MONTHLY";
  return passType.replace(/_/g, " ").toUpperCase();
}

function calculateRemainingValidity(purchasedAt: string | Date | undefined, passType: string | undefined) {
  if (!purchasedAt) return null;
  try {
    const purchaseDate = new Date(purchasedAt);
    let daysValid = 0;
    const type = (passType || "").toLowerCase();
    
    if (type.includes("monthly") || type.includes("month")) daysValid = 30;
    else if (type.includes("weekly") || type.includes("week") || type.includes("drop")) daysValid = 7;
    else if (type.includes("10-20")) daysValid = 30;

    if (daysValid === 0) return null;

    const expiryDate = new Date(purchaseDate);
    expiryDate.setDate(expiryDate.getDate() + daysValid);
    expiryDate.setHours(23, 59, 59, 999);

    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();

    if (diffMs <= 0) return "Expired";

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 30) return "1 Month";
    if (diffDays === 7) return "1 Week";
    if (diffDays > 0) return `${diffDays} Day${diffDays > 1 ? "s" : ""}`;
    
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (diffHours > 0) return `${diffHours} Hr${diffHours > 1 ? "s" : ""}`;
    
    return "Less than 1 hour";
  } catch (error) {
    return null;
  }
}

function countUsedCredits(scheduledDates?: string[] | null, classEndTime?: string | null) {
  if (!scheduledDates?.length || !classEndTime) return 0;

  const now = new Date();
  return scheduledDates.filter((date) => {
    const endAt = new Date(`${date}T${classEndTime}`);
    return !Number.isNaN(endAt.getTime()) && endAt < now;
  }).length;
}

function countUsedCreditsForPurchase(purchase: any, classList: any[], onlyCompleted: boolean = false) {
  const now = new Date();
  
  if (Array.isArray(purchase?.bookedSessions) && purchase.bookedSessions.length > 0) {
    if (!onlyCompleted) return purchase.bookedSessions.length;
    
    return purchase.bookedSessions.filter((session: any) => {
      const cls = classList.find((c: any) => c.id === session.classId);
      const endTime = cls?.endTime || purchase?.classEndTime || "23:59";
      const endAt = new Date(`${session.sessionDate}T${endTime}`);
      return !Number.isNaN(endAt.getTime()) && endAt < now;
    }).length;
  }

  const bookedSessionDates = Array.isArray(purchase?.bookedSessionDates)
    ? purchase.bookedSessionDates
    : purchase?.bookedSessionDate
      ? [purchase.bookedSessionDate]
      : [];

  if (bookedSessionDates.length > 0) {
    if (!onlyCompleted) return bookedSessionDates.length;
    
    return bookedSessionDates.filter((date: string) => {
      const endTime = purchase?.classEndTime || "23:59";
      const endAt = new Date(`${date}T${endTime}`);
      return !Number.isNaN(endAt.getTime()) && endAt < now;
    }).length;
  }

  const linkedClasses = classList.filter((cls: any) => {
    if (purchase?.classId && cls?.id === purchase.classId) return true;
    return Boolean(
      purchase?.className &&
      cls?.className &&
      cls.className.toLowerCase() === String(purchase.className).toLowerCase(),
    );
  });

  if (linkedClasses.length > 0) {
    if (!onlyCompleted) {
      // If we want total allocated, we count all scheduled dates for these classes
      let totalAllocated = 0;
      linkedClasses.forEach((linkedClass: any) => {
        const scheduledDates =
          Array.isArray(linkedClass.scheduledDates) && linkedClass.scheduledDates.length > 0
            ? linkedClass.scheduledDates
            : linkedClass.day
              ? [linkedClass.day]
              : [];
        totalAllocated += scheduledDates.length;
      });
      return totalAllocated;
    }

    const completedSessions = new Set<string>();
    linkedClasses.forEach((linkedClass: any) => {
      const scheduledDates =
        Array.isArray(linkedClass.scheduledDates) && linkedClass.scheduledDates.length > 0
          ? linkedClass.scheduledDates
          : linkedClass.day
            ? [linkedClass.day]
            : [];

      scheduledDates.forEach((date: string) => {
        const endTime = linkedClass.endTime || purchase?.classEndTime;
        if (!endTime) return;
        const endAt = new Date(`${date}T${endTime}`);
        if (!Number.isNaN(endAt.getTime()) && endAt < now) {
          completedSessions.add(`${date}|${endTime}`);
        }
      });
    });
    return completedSessions.size;
  }

  if (!onlyCompleted) {
    return (purchase?.classScheduledDates?.length || 0);
  }
  return countUsedCredits(purchase?.classScheduledDates, purchase?.classEndTime);
}

function getPassUsageColors(consumedCredits: number, totalCredits: number | null, isExpired: boolean) {
  if (isExpired) {
    return {
      chipBg: "#FEF3F2",
      chipColor: "#DC2626",
      progressColor: "#DC2626",
    };
  }

  if (totalCredits === null || totalCredits <= 0) {
    return {
      chipBg: "#f0f9e4",
      chipColor: "#a3d65c",
      progressColor: "#a3d65c",
    };
  }

  const usagePercent = (consumedCredits / totalCredits) * 100;

  if (usagePercent >= 90) {
    return {
      chipBg: "#FEF3F2",
      chipColor: "#DC2626",
      progressColor: "#DC2626",
    };
  }

  if (usagePercent >= 70) {
    return {
      chipBg: "#FFF7ED",
      chipColor: "#F59E0B",
      progressColor: "#F59E0B",
    };
  }

  return {
    chipBg: "#f0f9e4",
    chipColor: "#a3d65c",
    progressColor: "#a3d65c",
  };
}

function hasPassActuallyExpired(expiryDate?: string | null) {
  if (!expiryDate) return false;

  const parsedExpiry = new Date(expiryDate);
  if (Number.isNaN(parsedExpiry.getTime())) return false;

  return parsedExpiry.getTime() < Date.now();
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [passCarouselIndex, setPassCarouselIndex] = useState(0);
  const [passSlideDirection, setPassSlideDirection] = useState<"next" | "prev">("next");
  const { data: meData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });
  const today = useMemo(() => formatLocalDate(new Date()), []);
  const { data: classData } = useQuery(GET_CLASS_LIST, {
    variables: {
      page: 1,
      perPage: 100,
      includeCancelled: false,
    },
    fetchPolicy: "network-only",
  });
  const { data: myPassesData } = useQuery(GET_MY_PURCHASED_PASSES, {
    fetchPolicy: "network-only",
  });
  const { data: eventData } = useQuery(GET_EVENT_LIST, {
    variables: {
      page: 1,
      perPage: 100,
      startDate: today,
    },
    fetchPolicy: "network-only",
  });
  const { data: myEventBookingsData } = useQuery(GET_MY_EVENT_BOOKINGS, {
    fetchPolicy: "network-only",
  });

  const joinedState = useMemo(() => {
    const sessionKeys = new Set<string>();

    (myPassesData?.getMyPurchasedPasses?.data || []).forEach((purchase: any) => {
      if (purchase?.status !== "ACTIVE") return;
      if (Array.isArray(purchase?.bookedSessions) && purchase.bookedSessions.length > 0) {
        purchase.bookedSessions.forEach((session: any) => {
          if (session?.classId && session?.sessionDate) {
            sessionKeys.add(`${session.classId}|${session.sessionDate}`);
          }
        });
        return;
      }
      const bookedDates = Array.isArray(purchase?.bookedSessionDates)
        ? purchase.bookedSessionDates
        : purchase?.bookedSessionDate
          ? [purchase.bookedSessionDate]
          : [];
      if (purchase?.classId && bookedDates.length > 0) {
        bookedDates.forEach((date: string) => sessionKeys.add(`${purchase.classId}|${date}`));
      }
    });

    return { sessionKeys };
  }, [myPassesData]);

  const displayName = useMemo(() => {
    const fallbackUser = JSON.parse(localStorage.getItem("user") || "{}");
    const fullName = meData?.me?.fullname || fallbackUser?.fullname || "User";
    return fullName.trim().split(/\s+/)[0] || "User";
  }, [meData]);

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
      }),
    [],
  );
  const nextClassCard = useMemo<MiniCard>(() => {
    const now = new Date();
    const upcomingSessions = (classData?.getClassList?.data || [])
      .flatMap((cls: any) => {
        const scheduledDates = Array.isArray(cls.scheduledDates) && cls.scheduledDates.length > 0
          ? cls.scheduledDates
          : cls.day
            ? [cls.day]
            : [];

        return scheduledDates.map((date: string) => {
          const startsAt = new Date(`${date}T${cls.startTime}`);
          return {
            classId: cls.id,
            className: cls.className,
            instructorName: cls.instructorName,
            sessionDate: date,
            startsAt,
          };
        });
      })
      .filter((session: any) => {
        const sessionKey = `${session.classId}|${session.sessionDate}`;
        return joinedState.sessionKeys.has(sessionKey);
      })
      .filter((session: any) => !Number.isNaN(session.startsAt.getTime()) && session.startsAt >= now)
      .sort((a: any, b: any) => a.startsAt.getTime() - b.startsAt.getTime());

    const nextSession = upcomingSessions[0];

    if (!nextSession) {
      return {
        label: "Next Class",
        title: "No upcoming class",
        subtitle: "Book a class to see your next session here",
        icon: <CalendarTodayOutlinedIcon fontSize="small" />,
        iconBg: "#f0f9e4",
        iconColor: "#a3d65c",
        path: "/classes",
      };
    }

    const sessionDate = nextSession.startsAt.toISOString().split("T")[0];
    const dateLabel =
      sessionDate === today
        ? `Today at ${formatTo12Hour(nextSession.startsAt.toTimeString().slice(0, 5))}`
        : nextSession.startsAt.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });

    return {
      label: "Next Class",
      title: nextSession.className,
      subtitle: `${dateLabel} • ${nextSession.instructorName || "Instructor TBA"}`,
      icon: <CalendarTodayOutlinedIcon fontSize="small" />,
      iconBg: "#f0f9e4",
      iconColor: "#a3d65c",
      path: "/classes",
      state: {
        openClass: {
          classId: nextSession.classId,
          sessionDate: nextSession.sessionDate,
        },
      },
    };
  }, [classData, joinedState, today]);

  const activePassCard = useMemo<MiniCard>(() => {
    const activePasses = (myPassesData?.getMyPurchasedPasses?.data || []).filter(
      (purchase: any) => purchase?.status === "ACTIVE",
    );

    if (activePasses.length === 0) {
      return {
        label: "Active Pass Status",
        title: "No active pass",
        subtitle: "Purchase a pass to start booking classes",
        icon: <ConfirmationNumberOutlinedIcon fontSize="small" />,
        iconBg: "#E8F1FF",
        iconColor: "#2563EB",
        path: "/passes",
        state: { initialTab: "my_passes" },
      };
    }

    const activeCount = activePasses.length;
    return {
      label: "Active Pass Status",
      title: `${activeCount} Active Pass${activeCount === 1 ? "" : "es"}`,
      icon: <ConfirmationNumberOutlinedIcon fontSize="small" />,
      iconBg: "#E8F1FF",
      iconColor: "#2563EB",
      path: "/passes",
      state: { initialTab: "my_passes" },
    };
  }, [myPassesData]);

  const nextEventCard = useMemo<MiniCard>(() => {
    const now = new Date();
    const upcomingEvents = (eventData?.getEventList?.data || [])
      .map((event: any) => {
        const eventStart = new Date(event.startDate);
        return {
          ...event,
          eventStart,
        };
      })
      .filter((event: any) => !Number.isNaN(event.eventStart.getTime()) && event.eventStart >= now)
      .sort((a: any, b: any) => a.eventStart.getTime() - b.eventStart.getTime());

    const nextEvent = upcomingEvents[0];

    if (!nextEvent) {
      return {
        label: "Next Event",
        title: "No upcoming event",
        subtitle: "Explore events to find your next experience",
        icon: <Calendar size={18} />,
        iconBg: "#FFF7ED",
        iconColor: "#F59E0B",
        path: "/events",
      };
    }

    const dateLabel = nextEvent.eventStart.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    return {
      label: "Next Event",
      title: nextEvent.eventName,
      subtitle: `${dateLabel} • ${nextEvent.location || "Location TBA"}`,
      icon: <Calendar size={18} />,
      iconBg: "#FFF7ED",
      iconColor: "#F59E0B",
      path: "/events",
    };
  }, [eventData]);

  const weeklySchedule = useMemo(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const currentDay = currentDate.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() + mondayOffset);

    const weekDays = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + index);
      return {
        iso: formatLocalDate(date),
        label: date.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
        date: String(date.getDate()).padStart(2, "0"),
      };
    });

    const classDates = new Set<string>();
    (classData?.getClassList?.data || [])
      .forEach((cls: any) => {
        const scheduledDates =
          Array.isArray(cls.scheduledDates) && cls.scheduledDates.length > 0
            ? cls.scheduledDates
            : cls.day
              ? [cls.day]
              : [];
        scheduledDates.forEach((date: string) => {
          const sessionKey = `${cls.id}|${date}`;
          if (
            joinedState.sessionKeys.has(sessionKey)
          ) {
            classDates.add(date);
          }
        });
      });

    return {
      monthLabel: currentDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      }),
      todayIso: formatLocalDate(currentDate),
      weekDays: weekDays.map((day) => ({
        ...day,
        hasClass: classDates.has(day.iso),
      })),
    };
  }, [classData, joinedState]);

  const todayClasses = useMemo<TodayClassRow[]>(() => {
    const now = new Date();

    return (classData?.getClassList?.data || [])
      .flatMap((cls: any) => {
        const scheduledDates =
          Array.isArray(cls.scheduledDates) && cls.scheduledDates.length > 0
            ? cls.scheduledDates
            : cls.day
              ? [cls.day]
              : [];

        return scheduledDates
          .filter((date: string) => {
            if (date !== today) return false;
            const sessionKey = `${cls.id}|${date}`;
            return joinedState.sessionKeys.has(sessionKey);
          })
          .map((date: string): TodayClassRow => {
            const startsAt = new Date(`${date}T${cls.startTime}`);
            const endsAt = new Date(`${date}T${cls.endTime}`);
            let status: TodayClassRow["status"] = "Upcoming";
            if (now >= endsAt) {
              status = "Completed";
            } else if (now >= startsAt && now < endsAt) {
              status = "Ongoing";
            }
            return {
              id: `${cls.id}-${date}`,
              time: `${formatTo12Hour(cls.startTime)} - ${formatTo12Hour(cls.endTime)}`,
              className: cls.className,
              instructor: cls.instructorName || "Instructor TBA",
              status,
              startsAt,
            };
          });
      })
      .sort((a: TodayClassRow, b: TodayClassRow) => a.startsAt.getTime() - b.startsAt.getTime());
  }, [classData, joinedState, today]);

  const todayClassesHeading = useMemo(() => {
    const currentDate = new Date(`${today}T00:00:00`);
    const month = currentDate.toLocaleDateString("en-US", { month: "long" });
    const date = currentDate.getDate();
    const suffix =
      date % 10 === 1 && date !== 11
        ? "st"
        : date % 10 === 2 && date !== 12
          ? "nd"
          : date % 10 === 3 && date !== 13
            ? "rd"
            : "th";
    return `${month} ${date}${suffix} Classes`;
  }, [today]);

  const upcomingEventRows = useMemo<UpcomingEventCardRow[]>(() => {
    const bookedEventIds = new Set<string>();
    (myEventBookingsData?.getMyEventBookings?.data || []).forEach((booking: any) => {
      if (booking?.status === "CONFIRMED" && booking?.eventId) {
        bookedEventIds.add(booking.eventId);
      }
    });

    return (eventData?.getEventList?.data || [])
      .map((event: any) => ({
        ...event,
        start: new Date(event.startDate),
      }))
      .filter((event: any) => !Number.isNaN(event.start.getTime()) && event.start >= new Date())
      .sort((a: any, b: any) => a.start.getTime() - b.start.getTime())
      .slice(0, 2)
      .map((event: any) => {
        return {
          id: event.id,
          month: event.start.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
          day: String(event.start.getDate()).padStart(2, "0"),
          title: event.eventName,
          subtitle: `${event.location} • ${event.time ? event.time.split("-").map((time: string) => formatTo12Hour(time.trim())).join(" - ") : "Time TBA"}`,
          tag: bookedEventIds.has(event.id) ? "REGISTERED" : "VIEW DETAILS",
          tagColor: "#a3d65c",
        };
      });
  }, [eventData, myEventBookingsData]);

  const dashboardPassCards = useMemo<DashboardPassCard[]>(() => {
    const purchases = (myPassesData?.getMyPurchasedPasses?.data || []).filter(
      (purchase: any) => purchase?.status !== "FAILED" && purchase?.status !== "PENDING",
    );
    const classList = classData?.getClassList?.data || [];

    return purchases.map((purchase: any) => {
      const isExpired = hasPassActuallyExpired(purchase.expiryDate);
      const credits = typeof purchase.noOfClasses === "number" ? purchase.noOfClasses : null;
      const consumedCredits = credits !== null
        ? Math.min(credits, countUsedCreditsForPurchase(purchase, classList, true))
        : 0;
      const usageColors = getPassUsageColors(consumedCredits, credits, isExpired);

      return {
        id: purchase.id,
        title: purchase.passName || "Untitled Pass",
        subtitle: getPassTypeLabel(purchase.passType),
        chipLabel: `TOTAL: ${credits ?? 0}`,
        chipBg: "#EEF2F7",
        chipColor: "#64748B",
        remainingChipLabel: `${consumedCredits} USED`,
        remainingChipBg: usageColors.chipBg,
        remainingChipColor: usageColors.chipColor,
        metaLabel: credits !== null ? "Credits Balance" : "Linked Class",
        metaValue: credits !== null ? `${consumedCredits} / ${credits}` : purchase.className || "General access",
        progressValue: credits !== null && credits > 0 ? (consumedCredits / credits) * 100 : 0,
        progressColor: usageColors.progressColor,
        footer: purchase.className
          ? `Class: ${purchase.className}`
          : `Booked on ${new Date(purchase.purchasedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}`,
        expiryText: purchase.expiryDate 
          ? `Expires on ${new Date(purchase.expiryDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            })}`
          : "No expiry set",
        };
    });
  }, [classData, myPassesData]);

  const visibleDashboardPassCards = useMemo(() => {
    if (dashboardPassCards.length <= 2) return dashboardPassCards;

    const maxStartIndex = Math.max(0, dashboardPassCards.length - 2);
    const safeStartIndex = Math.min(passCarouselIndex, maxStartIndex);
    return dashboardPassCards.slice(safeStartIndex, safeStartIndex + 2);
  }, [dashboardPassCards, passCarouselIndex]);

  const handlePassCarouselNext = () => {
    setPassSlideDirection("next");
    setPassCarouselIndex((current) => (
      current + 2 >= dashboardPassCards.length ? 0 : current + 2
    ));
  };

  const handlePassCarouselPrev = () => {
    setPassSlideDirection("prev");
    setPassCarouselIndex((current) => (
      current - 2 < 0 ? Math.max(0, dashboardPassCards.length - 2) : current - 2
    ));
  };

  const miniCards = useMemo(
    () => [nextClassCard, activePassCard, nextEventCard],
    [activePassCard, nextClassCard, nextEventCard],
  );

  return (
    <Stack spacing={2.25} sx={{ mt: { xs: 1, sm: 2, md: 3 }, px: { xs: 1, sm: 0 }, pb: { xs: 2, sm: 0 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        gap={2}
        sx={{ pt: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: themestyle.colors.primary, lineHeight: 1.15 }}>
            Welcome back, {displayName}!
          </Typography>
          <Typography sx={{ mt: 0.5, color: themestyle.colors.textSecondary, fontWeight: 700, fontSize: 13 }}>
            Here is a quick look at your schedule and activity for {todayLabel}.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate("/classes")}
          sx={{
            textTransform: "none",
            fontWeight: 900,
            bgcolor: "#a3d65c",
            boxShadow: "none",
            borderRadius: 2,
            width: { xs: "100%", sm: "auto" },
            "&:hover": { bgcolor: "#699a26", boxShadow: "none" },
          }}
        >
          Book a New Class
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: { xs: 1.5, sm: 2 },
          "& > *": { minWidth: 0 },
        }}
      >
        {miniCards.map((c) => (
          <CardShell
            key={c.label}
            sx={
              c.path
                ? {
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      boxShadow: "0px 14px 28px rgba(7, 24, 51, 0.08)",
                    },
                  }
                : undefined
            }
          >
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                onClick={() => {
                  if (c.path) {
                    navigate(c.path, c.state ? { state: c.state } : undefined);
                  }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: c.iconBg,
                    color: c.iconColor,
                    display: "grid",
                    placeItems: "center",
                    flex: "0 0 auto",
                  }}
                >
                  {c.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                    {c.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary, lineHeight: 1.2 }} noWrap>
                    {c.title}
                  </Typography>
                  {c.subtitle ? (
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
                      {c.subtitle}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </CardContent>
          </CardShell>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 2,
          "& > *": { minWidth: 0 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <CardShell>
            <CardContent sx={{ p: { xs: 1.5, sm: 1.75 }, "&:last-child": { pb: { xs: 1.5, sm: 1.75 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarMonthOutlinedIcon sx={{ color: "#a3d65c" }} />
                  <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }}>
                    Weekly Schedule
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={{ xs: 0.75, sm: 2 }}
                >
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 800 }}>
                    {weeklySchedule.monthLabel}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => navigate("/calendar")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      color: "#a3d65c",
                      px: 0,
                      "&:hover": { bgcolor: "transparent" },
                    }}
                  >
                    For more details
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  mt: 1.5,
                  overflowX: { xs: "auto", sm: "visible" },
                  pl: { xs: 0.25, sm: 0 },
                  pr: { xs: 1, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 0.9,
                    minWidth: { xs: 420, sm: "auto" },
                  }}
                >
                  {weeklySchedule.weekDays.map((d) => {
                    const isToday = d.iso === weeklySchedule.todayIso;
                    return (
                    <Box
                      key={`${d.label}-${d.date}`}
                      onClick={() =>
                        navigate("/calendar", {
                          state: { selectedDate: d.iso },
                        })
                      }
                      sx={{
                        borderRadius: 1.75,
                        border: isToday ? `1.5px solid #a3d65c` : "1.5px solid transparent",
                        bgcolor: isToday ? "#f0f9e4" : "transparent",
                        py: 0.9,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "transform 0.2s ease, background-color 0.2s ease",
                        "&:hover": {
                          bgcolor: "#F0F9E4",
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 900, display: "block", fontSize: 10 }}>
                        {d.label}
                      </Typography>
                      <Typography sx={{ color: themestyle.colors.primary, fontWeight: 900, lineHeight: 1.1, fontSize: 16 }}>
                        {d.date}
                      </Typography>
                    </Box>
                  )})}
                </Box>
              </Box>
            </CardContent>
          </CardShell>

          <Typography sx={{ mt: 2.25, mb: 1.25, fontWeight: 900, color: themestyle.colors.primary }}>
            {todayClassesHeading}
          </Typography>

          <CardShell>
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ p: { xs: 1.75, sm: 2.25 } }}
              >
                <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }}>My Classes</Typography>
                <Button
                  variant="text"
                  onClick={() => navigate("/classes")}
                  sx={{ textTransform: "none", fontWeight: 900, color: "#a3d65c" }}
                >
                  View All
                </Button>
              </Stack>
              <Divider />
              <Box sx={{ px: { xs: 1.25, sm: 1.75 }, pb: { xs: 1.25, sm: 1.75 }, overflowX: "auto" }}>
                {todayClasses.length > 0 ? (
                  <Table size="small" sx={{ mt: 1, minWidth: 620 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#94A3B8", fontWeight: 900, fontSize: 11, borderBottom: "none" }}>
                          TIME
                        </TableCell>
                        <TableCell sx={{ color: "#94A3B8", fontWeight: 900, fontSize: 11, borderBottom: "none" }}>
                          CLASS NAME
                        </TableCell>
                        <TableCell sx={{ color: "#94A3B8", fontWeight: 900, fontSize: 11, borderBottom: "none" }}>
                          INSTRUCTOR
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#94A3B8", fontWeight: 900, fontSize: 11, borderBottom: "none" }}>
                          STATUS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todayClasses.map((r) => (
                        <TableRow key={r.id} sx={{ "& td": { borderBottom: "1px solid #F1F5F9" } }}>
                          <TableCell sx={{ color: "#64748B", fontWeight: 800 }}>{r.time}</TableCell>
                          <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900 }}>{r.className}</TableCell>
                          <TableCell sx={{ color: "#64748B", fontWeight: 700 }}>{r.instructor}</TableCell>
                          <TableCell align="right">{statusChip(r.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ color: "#64748B", fontWeight: 700, fontSize: 14 }}>
                      No classes scheduled for today.
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </CardShell>
        </Box>

        <CardShell sx={{ height: { xs: "auto", md: 414 }, minWidth: 0, width: "100%" }}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
              <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }}>Upcoming Events</Typography>
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 1.75, sm: 2.25 }, py: 2 }}>
              <Stack spacing={1.5}>
                {upcomingEventRows.length > 0 ? (
                  upcomingEventRows.map((e) => (
                    <Stack key={e.id} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 54,
                          borderRadius: 2,
                          bgcolor: "#f0f9e4",
                          border: "1px solid #a3d65c",
                          textAlign: "center",
                          py: 1,
                          flex: "0 0 auto",
                          lineHeight: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 900, color: "#a3d65c" }}>
                          {e.month}
                        </Typography>
                        <Typography sx={{ fontWeight: 900, color: "#a3d65c", fontSize: 18 }}>
                          {e.day}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            color: themestyle.colors.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontWeight: 700,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {e.subtitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          onClick={() => {
                            if (e.tag === "VIEW DETAILS") {
                              navigate("/events");
                            }
                          }}
                          sx={{
                            display: "block",
                            mt: 0.5,
                            fontWeight: 900,
                            color: e.tagColor,
                            cursor: e.tag === "VIEW DETAILS" ? "pointer" : "default",
                          }}
                        >
                          {e.tag}
                        </Typography>
                      </Box>
                    </Stack>
                  ))
                ) : (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ color: "#64748B", fontWeight: 700, fontSize: 14 }}>
                      No upcoming events.
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Button
                variant="contained"
                fullWidth={true}
                onClick={() => navigate("/events")}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: "#a3d65c",
                  color: "#fff",
                  boxShadow: "none",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "#699a26", boxShadow: "none" },
                }}
              >
                Explore More Events
              </Button>
            </Box>
          </CardContent>
        </CardShell>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }}>
            My Passes
          </Typography>
          {dashboardPassCards.length > 2 ? (
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                onClick={handlePassCarouselPrev}
                sx={{
                  color: "#4CAF50",
                  border: "1px solid #DDEAC7",
                  bgcolor: "#F4FBEA",
                  width: 34,
                  height: 34,
                  "&:hover": {
                    bgcolor: "#EAF6D6",
                  },
                }}
              >
                <ArrowBackIosNewRoundedIcon
                  sx={{
                    fontSize: 15,
                    color: "#4CAF50",
                    ml: 0.25,
                  }}
                />
              </IconButton>
              <IconButton
                size="small"
                onClick={handlePassCarouselNext}
                sx={{
                  color: "#4CAF50",
                  border: "1px solid #DDEAC7",
                  bgcolor: "#F4FBEA",
                  width: 34,
                  height: 34,
                  "&:hover": {
                    bgcolor: "#EAF6D6",
                  },
                }}
              >
                <ArrowForwardIosRoundedIcon
                  sx={{
                    fontSize: 16,
                    color: "#4CAF50",
                    ml: 0.25,
                  }}
                />
              </IconButton>
            </Stack>
          ) : null}
        </Stack>
        <Box
          key={`${passSlideDirection}-${passCarouselIndex}`}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            "& > *": { minWidth: 0 },
            animation: `${passSlideDirection === "next" ? "passSlideNext" : "passSlidePrev"} 280ms ease`,
            "@keyframes passSlideNext": {
              "0%": {
                opacity: 0.45,
                transform: "translateX(22px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateX(0)",
              },
            },
            "@keyframes passSlidePrev": {
              "0%": {
                opacity: 0.45,
                transform: "translateX(-22px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateX(0)",
              },
            },
          }}
        >
          {dashboardPassCards.length > 0 ? (
            visibleDashboardPassCards.map((pass) => (
              <CardShell key={pass.id}>
                <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }}>{pass.title}</Typography>
                      <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 900, letterSpacing: 1 }}>
                        {pass.subtitle}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                      {pass.remainingChipLabel ? (
                        <Chip
                          size="small"
                          label={pass.remainingChipLabel}
                          sx={{
                            height: 20,
                            fontWeight: 900,
                            bgcolor: pass.remainingChipBg || "#E8F1FF",
                            color: pass.remainingChipColor || "#2563EB",
                            fontSize: 10,
                          }}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        label={pass.chipLabel}
                        sx={{ height: 20, fontWeight: 900, bgcolor: pass.chipBg, color: pass.chipColor, fontSize: 10 }}
                      />
                    </Stack>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }} spacing={1}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 800 }}>
                      {pass.metaLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: themestyle.colors.primary, fontWeight: 900 }}>
                      {pass.metaValue}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pass.progressValue}
                    sx={{
                      mt: 1,
                      height: 8,
                      borderRadius: 999,
                      bgcolor: "#EEF2F7",
                      "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: pass.progressColor },
                    }}
                  />
                  <Typography variant="caption" sx={{ display: "block", mt: 1.25, color: "#94A3B8", fontWeight: 800 }}>
                    {pass.footer}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#94A3B8", fontWeight: 900 }}>
                    {pass.expiryText}
                  </Typography>
                </CardContent>
              </CardShell>
            ))
          ) : (
            <CardShell sx={{ gridColumn: { xs: "auto", md: "1 / span 2" } }}>
              <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
                <Typography sx={{ color: "#64748B", fontWeight: 700, fontSize: 14, textAlign: "center" }}>
                  No passes found.
                </Typography>
              </CardContent>
            </CardShell>
          )}
        </Box>
      </Box>
    </Stack>
  );
}
